/**
 * Opaque path-segment detection for Ryn Replay (WI 34328): the heuristic that
 * tells a secret-looking segment from a route name or id, and the run-split
 * redaction both URL scrubs use when an app opts in. Split out of
 * ryn-replay-url-scrub.ts (500-line gate), which re-exports the public names.
 */

const GUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const JWT_SEGMENT = /^[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}$/;
const TOKEN_SEGMENT = /^[A-Za-z0-9_~+=-]{20,}$/;
const SLUG_SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)+$/;

/**
 * True for a path segment that looks like a secret rather than a route name or
 * id (WI 34328): JWT-shaped, or 20+ token characters mixing letters and digits
 * (random tokens, base64url, hex digests). GUIDs, numeric ids, lowercase
 * kebab slugs and filenames are not opaque.
 */
export const isRynReplayOpaqueSegment = (segment: string): boolean => {
  if (GUID_SEGMENT.test(segment)) {
    return false;
  }
  if (JWT_SEGMENT.test(segment)) {
    return true;
  }
  if (!TOKEN_SEGMENT.test(segment) || SLUG_SEGMENT.test(segment)) {
    return false;
  }
  return /[A-Za-z]/.test(segment) && /\d/.test(segment);
};

const TOKEN_CHAR = /[A-Za-z0-9_~+=-]/;

/**
 * A raw segment that ends in a LITERAL dot and a short extension (optionally
 * `:line:col` and closing punctuation, as in a stack frame) is a filename.
 * Read from the raw text, so an encoded `%2E` never earns it (PR 4516 review).
 */
const FILENAME_TAIL = /\.[A-Za-z0-9]{1,5}(?::\d+){0,2}[)\]'"`,;!]*$/;

/** Literal characters and %XX escapes, each with the byte it decodes to. */
const toSegmentUnits = (raw: string): { raw: string; ch: string }[] =>
  [...raw.matchAll(/%[0-9A-Fa-f]{2}|[\s\S]/g)].map(([unit]) => ({
    raw: unit,
    ch:
      unit.length === 3
        ? String.fromCharCode(parseInt(unit.slice(1), 16))
        : unit,
  }));

/**
 * Redact the opaque parts of one raw path segment (WI 34328, PR 4516 review
 * round 3). The segment is decoded unit by unit and split into maximal token
 * runs; any opaque run is redacted, whatever byte, literal or encoded, sits
 * before or after it. A whole-segment JWT is redacted as one. A filename
 * (raw {@link FILENAME_TAIL}) is kept. Each opaque part becomes `redacted`;
 * returns the same string when nothing is opaque.
 */
export const redactRynReplayOpaqueRuns = (
  raw: string,
  redacted: string
): string => {
  if (!raw || FILENAME_TAIL.test(raw)) {
    return raw;
  }
  const units = toSegmentUnits(raw);
  if (isRynReplayOpaqueSegment(units.map((u) => u.ch).join(''))) {
    return redacted;
  }
  let out = '';
  let run: typeof units = [];
  const flush = () => {
    out += isRynReplayOpaqueSegment(run.map((u) => u.ch).join(''))
      ? redacted
      : run.map((u) => u.raw).join('');
    run = [];
  };
  for (const unit of units) {
    if (TOKEN_CHAR.test(unit.ch)) {
      run.push(unit);
    } else {
      flush();
      out += unit.raw;
    }
  }
  flush();
  return out;
};

/**
 * True when any part of a raw URL path segment is opaque. The segment is
 * split on encoded `/`, `?` and `#` first, as the console-text pass splits,
 * so the filename exemption is tested per part and `<token>%3Fa.pdf` cannot
 * borrow the tail's extension (PR 4516 review round 4).
 */
export const hasRynReplayOpaquePart = (raw: string): boolean =>
  raw
    .split(/%2F|%3F|%23/i)
    .some((part) => redactRynReplayOpaqueRuns(part, '') !== part);
